'use client';

import React from 'react';
import Border from '@/components/common/border';
import ActivityTitle from '@/components/common/ActivityTitle';
import ImageCarousel from '@/components/common/ImageCarousel';
import InteractivePopover from '@/components/common/InteractivePopover';
import { OptimizedImage } from '@/components/common/OptimizedImage';
import { ExperienceType } from '../types';
import type { PackageTransfer } from '@/types/itineraryTypes';
import type { Vehicle } from '@/components/packages/Vehicles';

interface ExperienceTransfersProps {
  experience: unknown;
  type: ExperienceType;
}

const parseTransferPoint = (
  point: string,
  hotels: Array<{ _id?: string; uid?: string; name?: string }>,
  airports: Array<{ _id: string; name?: string; code?: string }>
) => {
  const [rawType, rawId] = point.split(':');
  const normalizedType = rawType === 'airport' ? 'airport' : 'hotel';
  const id = (rawId || '').trim();

  if (!id) return 'Unknown';

  if (normalizedType === 'airport') {
    const airport = airports.find(a => a._id === id);
    return airport?.name || airport?.code || id;
  }

  const hotel = hotels.find(h => h._id === id || h.uid === id);
  return hotel?.name || id;
};

export const ExperienceTransfers: React.FC<ExperienceTransfersProps> = ({
  experience,
  type,
}) => {
  if (!experience || type !== 'package') return null;

  const exp = experience as {
    transfers?: PackageTransfer[];
    vehicles?: Vehicle[];
    hotels?: Array<{ _id?: string; uid?: string; name?: string }>;
    airports?: Array<{ _id: string; name?: string; code?: string }>;
  };

  if (!Array.isArray(exp?.transfers) || exp.transfers.length === 0) return null;

  const vehiclesById = new Map(
    (exp.vehicles || [])
      .filter(vehicle => vehicle?._id)
      .map(vehicle => [vehicle._id as string, vehicle])
  );

  const hotels = Array.isArray(exp.hotels) ? exp.hotels : [];
  const airports = Array.isArray(exp.airports) ? exp.airports : [];

  const transferCards = exp.transfers
    .map((transfer, idx) => {
      if (!transfer?.vehicleId) return null;
      const vehicle = vehiclesById.get(transfer.vehicleId);
      if (!vehicle) return null;

      const fromName = parseTransferPoint(
        transfer.pickUpPoint,
        hotels,
        airports
      );
      const toName = parseTransferPoint(
        transfer.dropOffPoint,
        hotels,
        airports
      );

      return {
        key: `${transfer._id || idx}-${vehicle._id || idx}`,
        transferType: transfer.type,
        vehicle,
        fromName,
        toName,
        duration: transfer.duration,
      };
    })
    .filter(Boolean) as Array<{
    key: string;
    vehicle: Vehicle;
    fromName: string;
    toName: string;
    transferType?: string;
    duration?: string;
  }>;

  if (transferCards.length === 0) return null;

  return (
    <div className='mt-6'>
      <ActivityTitle title={'Transfers'} className='' />
      <div className='flex flex-wrap gap-5 pb-2 mt-2 w-full'>
        {transferCards.map((card, idx) => (
          <InteractivePopover
            key={card.key}
            triggerClassName='cursor-pointer flex-shrink-0 w-[180px]'
            contentClassName='w-[320px] p-0 !rounded-[15px] !border-white border shadow-[0px_0px_5px_1px_rgba(0,0,0,0.15)] overflow-hidden'
            trigger={
              <>
                <div
                  className='relative mb-2 overflow-hidden rounded-lg'
                  style={{ width: '180px', height: '90px' }}
                >
                  <OptimizedImage
                    src={
                      card.vehicle.imageUrl?.[0] ||
                      '/assets/images/No-Image.jpg'
                    }
                    alt={card.vehicle.title}
                    fill
                    style={{ objectFit: 'cover' }}
                    className='rounded-lg'
                    loading='eager'
                    priority={idx < 2}
                    disableBlobCache={true}
                  />
                </div>
                <h4
                  className='text-[12px] font-normal text-black leading-[15px] mb-1'
                  style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {card.fromName} to {card.toName}
                </h4>
                <p className='text-[10px] text-darkgrey'>
                  {card.vehicle.title}
                </p>
              </>
            }
            content={
              <div className='bg-white overflow-hidden flex flex-col max-h-[420px] sm:max-h-[380px]'>
                <div className='relative overflow-hidden flex-shrink-0 w-full h-[72px] sm:h-[120px]'>
                  <ImageCarousel
                    images={card.vehicle.imageUrl || []}
                    alt={card.vehicle.title}
                    className='h-full w-full'
                    imageClassName='object-cover'
                    showNavigation={true}
                    navigationId={`transfer-overview-${idx}`}
                    buttonSize='sm'
                    loop={false}
                    enableFullscreen={true}
                    fullscreenTitle={`${card.fromName} to ${card.toName}`}
                  />
                </div>
                <div className='px-3 py-2 space-y-2 overflow-y-auto'>
                  <h3 className='text-[14px] font-semibold text-black leading-[17px]'>
                    {card.fromName} to {card.toName}
                  </h3>
                  <p className='text-[12px] text-darkgrey leading-[16px]'>
                    Vehicle: {card.vehicle.title}
                  </p>
                  <div className='grid grid-cols-2 gap-2 text-[11px] text-darkgrey'>
                    {card.duration && <span>Duration: {card.duration}</span>}
                    {card.transferType && (
                      <span className='capitalize'>
                        Transfer: {card.transferType}
                      </span>
                    )}
                    {card.vehicle.type && (
                      <span className='capitalize'>
                        Type: {card.vehicle.type}
                      </span>
                    )}
                    {card.vehicle.seatingCapacity > 0 && (
                      <span>Seats: {card.vehicle.seatingCapacity}</span>
                    )}
                  </div>
                  {Array.isArray(card.vehicle.features) &&
                    card.vehicle.features.length > 0 && (
                      <div className='pt-1 border-t border-gray-100'>
                        <p className='text-[11px] font-medium text-black mb-1'>
                          Overview
                        </p>
                        <div className='flex flex-wrap gap-1'>
                          {card.vehicle.features.map(
                            (feature, featureIndex) => (
                              <span
                                key={`${feature}-${featureIndex}`}
                                className='text-[10px] px-2 py-0.5 bg-gray-100 text-gray-700 rounded border border-gray-200'
                              >
                                {feature}
                              </span>
                            )
                          )}
                        </div>
                      </div>
                    )}
                </div>
              </div>
            }
          />
        ))}
      </div>
      <Border />
    </div>
  );
};
